import React, { useContext, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useHistory, useLocation } from "react-router-dom";
import { FormattedMessage, injectIntl } from "react-intl";
import { useLang } from "../../../../_metronic/i18n/Metronici18n";
import { FlitServiceContext } from '../../../services/flitService';

function SetPassword({ intl }) {
  const lang = useLang();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const { flitSvc } = useContext(FlitServiceContext);
  // Get data from location state (passed from login page)
  const email = location.state?.email || "";
  const firstName = location.state?.firstName || "";
  const lastName = location.state?.lastName || "";
  const migrationMessage = location.state?.message || "";

  const SetPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: "AUTH.VALIDATION.INVALID_EMAIL" }))
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })),
    password: Yup.string()
      .min(8, intl.formatMessage({ id: "AUTH.VALIDATION.MIN_LENGTH_FIELD" }, { min: '8' }))
      .max(15, intl.formatMessage({ id: "AUTH.VALIDATION.MAX_LENGTH_FIELD" }, { max: '15' }))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,15}$/,
        intl.formatMessage({ id: "AUTH.VALIDATION.PASSWORD_REQIOREMENT" })
      )
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })),
    confirmPassword: Yup.string()
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" }))
      .oneOf(
        [Yup.ref("password")],
        intl.formatMessage({ id: "AUTH.VALIDATION.PASSWORD_MATCH" })
      ),
  });

  const formik = useFormik({
    initialValues: {
      email: email,
      password: "",
      confirmPassword: "",
    },
    validationSchema: SetPasswordSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      
      try {
        const { data } = await flitSvc.setPassword(
          values.email,
          values.password,
          values.confirmPassword,
          lang
        );

        setStatus({
          type: 'success',
          message: data.message || intl.formatMessage({ id: "AUTH.SET_PASSWORD.SUCCESS" })
        });
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          history.push('/auth/login');
        }, 2000);

      } catch (error) {
        setStatus({
          type: 'error',
          message: error.response?.data?.message || intl.formatMessage({ id: "AUTH.SET_PASSWORD.ERROR" })
        });
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }
    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }
    return "";
  };

  return (
    <div className="login-form login-forgot" style={{ display: "block" }}>
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">
          {firstName ? (
            <FormattedMessage 
              id="AUTH.SET_PASSWORD.TITLE_WITH_NAME" 
              defaultMessage="Welcome Back, {name}!"
              values={{ name: firstName }}
            />
          ) : (
            <FormattedMessage 
              id="AUTH.SET_PASSWORD.TITLE" 
              defaultMessage="Set Your Password"
            />
          )}
        </h3>
        <div className="text-muted font-weight-bold">
          {migrationMessage || (
            <FormattedMessage 
              id="AUTH.SET_PASSWORD.DESC" 
              defaultMessage="We've upgraded our system. Please set a new password to continue accessing your account." 
            />
          )}
        </div>
      </div>

      <form
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp"
      >
        {formik.status && (
          <div className={`mb-10 alert alert-custom alert-light-${formik.status.type === 'success' ? 'success' : 'danger'} alert-dismissible`}>
            <div className="alert-text font-weight-bold">
              {formik.status.message}
            </div>
          </div>
        )}

        <div className="form-group fv-plugins-icon-container">
          <input
            type="email"
            placeholder={intl.formatMessage({ id: "AUTH.INPUT.EMAIL" })}
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("email")}`}
            name="email"
            readOnly
            {...formik.getFieldProps("email")}
          />
          {formik.touched.email && formik.errors.email && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.email}</div>
            </div>
          )}
        </div>

        <div className="form-group fv-plugins-icon-container">
          <input
            type="password"
            placeholder={intl.formatMessage({ id: "AUTH.INPUT.PASSWORD" })}
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("password")}`}
            name="password"
            autoComplete="new-password"
            {...formik.getFieldProps("password")}
          />
          {formik.touched.password && formik.errors.password && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.password}</div>
            </div>
          )}
        </div>

        <div className="form-group fv-plugins-icon-container">
          <input
            type="password"
            placeholder={intl.formatMessage({ id: "AUTH.INPUT.CONFIRM_PASSWORD" })}
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("confirmPassword")}`}
            name="confirmPassword"
            autoComplete="new-password"
            {...formik.getFieldProps("confirmPassword")}
          />
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.confirmPassword}</div>
            </div>
          )}
        </div>

        <div className="form-group d-flex flex-wrap flex-center">
          <button
            id="kt_login_set_password_submit"
            type="submit"
            className="btn btn-primary font-weight-bold px-9 py-4 my-3 mx-4"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            <span>
              <FormattedMessage 
                id="AUTH.SET_PASSWORD.BUTTON" 
                defaultMessage="Set Password" 
              />
            </span>
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </button>

          <button
            type="button"
            onClick={() => history.push('/auth/login')}
            className="btn btn-light-primary font-weight-bold px-9 py-4 my-3 mx-4"
          >
            <FormattedMessage 
              id="AUTH.GENERAL.BACK_BUTTON" 
              defaultMessage="Back to Login"
            />
          </button>
        </div>
      </form>
    </div>
  );
}

export default injectIntl(SetPassword);